import { AlertCircle } from "lucide-react";
import { useEffect, useRef } from "react";

import { maskBrebKeyForDisplay } from "@/modules/dashboard/breb-qr-pay/utils/brebQrPayment";
import { ColocaAlert } from "@/shared/components/feedback/ColocaAlert";
import { useTaxLabel } from "@/shared/hooks/useTaxLabel";
import useTranslations from "@/shared/lang/useTranslations";
import { cn } from "@/shared/lib/utils";
import { formatClabe } from "@/shared/utils/clabe";
import { compareDecimalStrings } from "@/shared/utils/decimal";
import { formatPixInput } from "@/shared/utils/pixKey";
import { formatRail } from "@/shared/utils/railDisplay";

import type { NativeQuote } from "../../services/paymentsQuotes.service";
import { displayBrebKey } from "../../utils/brebKeyLookup";
import {
  formatReadableRate,
  isCrossCurrencySend,
  type SendBreakdown,
  sendBreakdown,
  type SendRecipient,
  type SendRoute,
} from "../../utils/walletSend";
import { formatSendMoney, formatSendMoneyWithCode } from "./formatSendMoney";
import { QuoteValidity } from "./QuoteValidity";

export interface SendReviewProps {
  quote: NativeQuote;
  route: SendRoute;
  recipient: SendRecipient;
  concept: string;
  /** The figures when the review opened; the price is renewed silently, so a change is said out loud. */
  reviewed: SendBreakdown | null;
}

interface RowProps {
  label: string;
  value: string;
  mono?: boolean;
  strong?: boolean;
}

function Row({ label, value, mono, strong }: RowProps) {
  return (
    <div className="flex items-baseline justify-between gap-4 py-1.5">
      <dt className="shrink-0 text-sm text-muted-foreground">{label}</dt>
      <dd
        className={cn(
          "min-w-0 truncate text-right text-sm text-foreground",
          mono ? "font-mono tabular-nums" : "font-medium",
          strong && "font-bold",
        )}
      >
        {value}
      </dd>
    </div>
  );
}

const PIX_TYPE_LABEL = {
  PHONE: "step3_pix_type_phone",
  EMAIL: "step3_pix_type_email",
  EVP: "step3_pix_type_evp",
} as const;

export function SendReview({ quote, route, recipient, concept, reviewed }: SendReviewProps) {
  const { t, activeLanguage } = useTranslations("dispersions");
  const taxLabel = useTaxLabel();
  const { t: tQr } = useTranslations("brebQrPay");
  const headingRef = useRef<HTMLParagraphElement>(null);
  const breakdown = sendBreakdown(quote);
  const crossCurrency = isCrossCurrencySend(route);
  const hasCost = compareDecimalStrings(breakdown.cost, "0") > 0;
  const hasConversion = breakdown.conversion !== null && compareDecimalStrings(breakdown.conversion, "0") > 0;
  const isFree = !hasCost && !hasConversion && breakdown.taxes.length === 0;
  const changed =
    !!reviewed &&
    (compareDecimalStrings(reviewed.debit, breakdown.debit) !== 0 ||
      compareDecimalStrings(reviewed.arrives, breakdown.arrives) !== 0);
  // Two currencies on one card: each figure carries its code, since "$" alone is pesos and dollars at once.
  const inDestination = (value: string) =>
    crossCurrency
      ? formatSendMoneyWithCode(value, route.currency, activeLanguage)
      : formatSendMoney(value, route.currency, activeLanguage);
  const inOrigin = (value: string) =>
    crossCurrency
      ? formatSendMoneyWithCode(value, route.originCurrency, activeLanguage)
      : formatSendMoney(value, route.originCurrency, activeLanguage);
  const rate = crossCurrency ? formatReadableRate(quote, activeLanguage) : null;
  const holder =
    recipient.rail === "BREB"
      ? (recipient.holderName ?? displayBrebKey(recipient.keyType, recipient.keyValue))
      : recipient.beneficiaryName;

  useEffect(() => {
    headingRef.current?.focus();
  }, []);

  const destinationRows = (() => {
    switch (recipient.rail) {
      case "BREB":
        return (
          <>
            {recipient.bankName && <Row label={t("send_review_bank")} value={recipient.bankName} />}
            <Row
              label={t("send_review_key")}
              value={
                recipient.qr
                  ? maskBrebKeyForDisplay(recipient.keyType, recipient.keyValue)
                  : displayBrebKey(recipient.keyType, recipient.keyValue)
              }
              mono
            />
            {recipient.qr?.reference && (
              <Row label={tQr("payee_reference")} value={recipient.qr.reference} mono />
            )}
          </>
        );
      case "TRANSFIYA":
        return (
          <>
            <Row label={t("send_review_bank")} value={recipient.bankName} />
            <Row label={t("send_review_account")} value={`••• ${recipient.accountNumber.slice(-4)}`} />
            <Row
              label={t("send_review_document")}
              value={`${recipient.documentType} ${recipient.documentNumber}`}
            />
          </>
        );
      case "PIX": {
        const typeKey =
          recipient.keyType === "CPF" || recipient.keyType === "CNPJ" ? null : recipient.keyType;
        return (
          <>
            <Row
              label={t("step3_pix_type_label")}
              value={typeKey ? t(PIX_TYPE_LABEL[typeKey]) : recipient.keyType}
            />
            <Row
              label={t("step3_pix_key_label")}
              value={formatPixInput(recipient.keyType, recipient.keyValue)}
              mono
            />
          </>
        );
      }
      case "SPEI":
        return (
          <>
            <Row label={t("step3_clabe_label")} value={formatClabe(recipient.clabe)} mono />
            {recipient.rfc && <Row label={t("send_review_rfc")} value={recipient.rfc} mono />}
          </>
        );
      case "CBU_CVU":
        return (
          <>
            <Row label={recipient.accountType} value={recipient.accountNumber} mono />
            {recipient.cuitCuil && <Row label={t("step2_cuit_label")} value={recipient.cuitCuil} mono />}
          </>
        );
    }
  })();

  return (
    <div className="space-y-4">
      {changed && <ColocaAlert variant="warning" icon={AlertCircle} text={t("send_cost_changed")} />}
      <section className="overflow-hidden rounded-2xl border border-border bg-card">
        <div className="bg-primary/5 px-5 pt-5 pb-4">
          <p
            ref={headingRef}
            tabIndex={-1}
            className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground outline-none"
          >
            {t("send_review_arrives_to")}
          </p>
          <p className="mt-1 truncate text-base font-semibold text-foreground">{holder}</p>
          <p className="mt-2 font-mono text-3xl font-bold tracking-tight text-foreground tabular-nums">
            {formatSendMoney(breakdown.arrives, route.currency, activeLanguage)}
            <span className="ml-2 text-sm font-medium tracking-wider text-muted-foreground">
              {route.currency}
            </span>
          </p>
        </div>
        <dl className="border-t border-dashed border-border px-5 py-3">
          {destinationRows}
          <Row label={t("send_review_via")} value={formatRail(recipient.rail)} />
          {concept.trim() && <Row label={t("send_review_concept")} value={concept.trim()} />}
        </dl>
        <dl className="border-t border-border bg-muted/30 px-5 py-3">
          {rate && (
            <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1 py-1.5">
              <dt className="text-sm text-muted-foreground">{t("send_rate_label")}</dt>
              <dd className="flex flex-col items-end gap-0.5">
                <span className="font-mono text-sm text-foreground tabular-nums">{rate}</span>
                <QuoteValidity expiresAt={quote.expires_at} />
              </dd>
            </div>
          )}
          {(hasCost || isFree) && (
            <Row
              label={t("send_row_cost")}
              value={hasCost ? inDestination(breakdown.cost) : t("send_cost_free")}
              mono={hasCost}
            />
          )}
          {breakdown.taxes.map((tax) => (
            <Row key={tax.code} label={taxLabel(tax)} value={inDestination(tax.amount)} mono />
          ))}
          {hasConversion && breakdown.conversion && (
            <Row label={t("send_row_conversion")} value={inOrigin(breakdown.conversion)} mono />
          )}
          <Row label={t("send_row_debit")} value={inOrigin(breakdown.debit)} mono strong />
        </dl>
      </section>
    </div>
  );
}
